import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthSplitLayout from './AuthSplitLayout';
import { useSnackbar } from 'notistack';
import { betterAuthClient } from '@/libs/better-auth-client';
import { getRedirectPath, redirectAfterAuth } from './auth-utils';
import TwoFactorEnroll from '@/components/personal/security/TwoFactorEnroll';

type AuthTwoFactorSetupLocationState = {
    redirectPath?: string;
    password?: string;
    oauthQuery?: string;
} | null;

export default function AuthTwoFactorSetup() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();

    const locationState = location.state as AuthTwoFactorSetupLocationState;
    const redirectPath = useMemo(
        () => locationState?.redirectPath || getRedirectPath(),
        [locationState?.redirectPath],
    );
    const prefillPassword = locationState?.password;
    const oauthQuery = locationState?.oauthQuery;

    type BetterAuthResult<TData> = {
        data?: TData | null;
        error?: {
            message?: string;
        } | null;
    };

    const handleDone = () => {
        if (!oauthQuery) {
            redirectAfterAuth(redirectPath);
            return;
        }

        void betterAuthClient.oauth2
            .continue({ postLogin: true, oauth_query: oauthQuery })
            .then((result: BetterAuthResult<{ url?: string }>) => {
                if (result.error) {
                    enqueueSnackbar(
                        typeof result.error.message === 'string'
                            ? result.error.message
                            : t('auth.oauth.error.default'),
                        { variant: 'error' },
                    );
                    redirectAfterAuth(redirectPath);
                    return;
                }

                const redirectUrl = result.data?.url;
                if (redirectUrl) {
                    window.location.replace(redirectUrl);
                    return;
                }

                redirectAfterAuth(redirectPath);
            })
            .catch(() => {
                redirectAfterAuth(redirectPath);
            });
    };

    return (
        <AuthSplitLayout>
            <Box mb={2}>
                <Typography
                    component={Link}
                    to="/auth/login"
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    <ArrowBackRounded sx={{ fontSize: 16 }} />
                    {t('auth.two-factor-setup.back-to-login')}
                </Typography>
            </Box>

            {/* requiresLegacyConfirm is always true here — the only entry point to this
                page is the re-enrollment redirect in AuthLoginCredentials, meaning the user
                always has a staged legacy secret to confirm first. Skip is intentionally
                omitted to make re-enrollment mandatory. */}
            <TwoFactorEnroll
                requiresLegacyConfirm={true}
                onComplete={handleDone}
                prefillPassword={prefillPassword}
            />
        </AuthSplitLayout>
    );
}
